import React from "react";
import {
  DatePicker,
  Divider,
  Dropdown,
  Select,
  TextField,
} from "@hid-galaxy-ui/galaxy-react";
import { CountryCode } from "../../../../utils/CountryCode";
import { getRevenueStreamSagaAction } from "../../../../redux/saga/partner-details/saga-actions";
import { getWorkPackagesSagaAction } from "../../../../redux/saga/external-contract/saga-actions";
import { getTechnologiesSagaAction } from "../../../../redux/saga/technology-settings/technologies/saga-actions";
import { useAppDispatch, useAppSelector } from "../../../../redux";
import { add, subDays } from "date-fns";
import { WorkPackagesType } from "../../../../redux/slices/external-contract/Types";
import { Constants } from "../../../../utils/constants";
import "./ExternalContractForm.scss";

interface ExternalContractFormInterface {
  contractDetails: {
    companyName: string;
    salesforceAccount: string;
    accountManagerEmail: string;
    oracleChannel: string;
    country: string;
    region: string;
    soNumber: string;
    salesforceOpportunity: string;
    typeOfEngagement: string;
    revenueStream: string;
    workPackageType: string;
    workPackageAmount: string;
    overallExpiryDate: Date | null;
    supportExpiryDate: Date | null;
    startDate: Date | null;
  };
  setContractDetails: React.Dispatch<React.SetStateAction<any>>;
  formPage: "General & Sales Information" | "Engagement & Expiry";
}

interface Option {
  label: string;
  value: string;
}
interface GroupedOptions {
  label: string;
  value: string;
  options?: Option[];
}

const transformWorkPackages = (workPackage: WorkPackagesType[]) => {
  const grouped: Record<string, Option[]> = workPackage.reduce(
    (acc: any, pkg) => {
      if (!acc[pkg.description.trim()]) {
        acc[pkg.description.trim()] = [];
      }
      acc[pkg.description.trim()].push({
        label: `${pkg.value}${
          pkg.metadata.part_number ? ` (${pkg.metadata.part_number})` : ""
        }`,
        value: pkg.value.trim(),
      });
      return acc;
    },
    {}
  );
  return Object.keys(grouped).map((key) => ({
    label: key,
    value: key,
    options: grouped[key],
  }));
};

const findSelectedOption = (
  optionsArray: GroupedOptions[],
  value: string
): Option | undefined => {
  for (const group of optionsArray) {
    const foundOption = group.options.find((option) => option.value === value);
    if (foundOption) return foundOption;
  }
  return undefined;
};

const CountryOptions = [
  { label: "Global", value: "Global" },
  ...CountryCode.map((country) => {
    return { label: country.name, value: country.name };
  }),
];

const RegionOptions = Constants.REGIONS.map((region) => {
  return { label: region, value: region };
});

const ExternalContractForm: React.FC<ExternalContractFormInterface> = ({
  contractDetails,
  setContractDetails,
  formPage,
}) => {
  const [country, setCountry] = React.useState(
    CountryOptions.find((county) => county.value === contractDetails.country)
  );
  const [emailStatus, setEmailStatus] = React.useState<
    { status: "success" | "error" } | {}
  >({});
  const [errorMsg, setErrorMsg] = React.useState({
    emailMsg: "",
  });
  const { revenueStream } = useAppSelector((state) => state.partner_details);
  const { workPackages } = useAppSelector((state) => state.external_contract);
  const technologies = useAppSelector(
    (state) => state.technology_settings_technologies.technologies
  );
  const WorkPackageTypeOptions: GroupedOptions[] =
    transformWorkPackages(workPackages);
  const [workPackage, setWorkPackage] = React.useState(
    findSelectedOption(WorkPackageTypeOptions, contractDetails.workPackageType)
  );
  const dispatch = useAppDispatch();
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  const RevenueStreamOptions: Array<{ label: string; value: string }> =
    revenueStream.map((item) => {
      return {
        label: item.value,
        value: item.value,
      };
    });
  const selectedWorkPackageType = workPackages.find(
    (workPackage) => workPackage.value === contractDetails.workPackageType
  );
  const TypeOfEngagementsOptions: Array<{ label: string; value: string }> =
    React.useMemo(
      () =>
        technologies
          .filter((technology) => technology.status === "Active")
          .map((technology) => ({
            label: technology.name,
            value: technology.name,
          })),
      [technologies]
    );

  React.useEffect(() => {
    dispatch(getRevenueStreamSagaAction());
    dispatch(getWorkPackagesSagaAction());
    dispatch(getTechnologiesSagaAction());
  }, []);

  React.useEffect(() => {
    setCountry(
      CountryOptions.find((county) => county.value === contractDetails.country)
    );
  }, [contractDetails]);

  React.useEffect(() => {
    if (emailRegex.test(contractDetails.accountManagerEmail)) {
      setEmailStatus({});
      setErrorMsg({
        ...errorMsg,
        emailMsg: "",
      });
    }
  }, [contractDetails.accountManagerEmail]);

  // Set default type of engagement from technologies settings
  React.useEffect(() => {
    if (!contractDetails.typeOfEngagement && TypeOfEngagementsOptions.length) {
      setContractDetails({
        ...contractDetails,
        typeOfEngagement: TypeOfEngagementsOptions[0].value,
      });
    }
  }, [TypeOfEngagementsOptions]);

  // Set default revenue stream
  React.useEffect(() => {
    if (!contractDetails.revenueStream && revenueStream.length) {
      setContractDetails({
        ...contractDetails,
        revenueStream: revenueStream[0].value,
      });
    }
  }, [revenueStream]);

  const validateEmail = () => {
    if (!emailRegex.test(contractDetails.accountManagerEmail)) {
      setEmailStatus({ status: "error" });
      setErrorMsg({
        ...errorMsg,
        emailMsg: "Enter valid email-id",
      });
    }
  };

  const handleChange = (event: React.FormEvent<HTMLInputElement>): void => {
    if (
      ["oracleChannel", "soNumber", "workPackageAmount"].includes(
        event.currentTarget.name
      )
    ) {
      const digitRegex = /^\d+$/;
      if (
        event.currentTarget.value.length === 0 ||
        digitRegex.test(event.currentTarget.value)
      ) {
        setContractDetails({
          ...contractDetails,
          [event.currentTarget.name]: event.currentTarget.value,
        });
      }
    } else {
      setContractDetails({
        ...contractDetails,
        [event.currentTarget.name]: event.currentTarget.value,
      });
    }
  };

  const calculateExpiryDates = React.useCallback(
    (startDate: Date, workPackageType: string) => {
      const selectedWorkPackageType = workPackages.find(
        (workPackage) => workPackage.value.trim() === workPackageType
      );

      if (!selectedWorkPackageType)
        return { supportExpiryDate: null, overallExpiryDate: null };

      if (selectedWorkPackageType.metadata.support_expiry) {
        const supportValidity = Number(
          selectedWorkPackageType.metadata.support_expiry.split(" ")[0]
        );
        const overallValidity = Number(
          selectedWorkPackageType.metadata.overall_expiry
            ? selectedWorkPackageType.metadata.overall_expiry.split(" ")[0]
            : selectedWorkPackageType.metadata.support_expiry.split(" ")[0]
        );

        const supportExpiryDate = subDays(
          add(startDate, { months: supportValidity }),
          1
        );
        const overallExpiryDate = subDays(
          add(startDate, { months: overallValidity }),
          1
        );

        return { supportExpiryDate, overallExpiryDate };
      } else if (selectedWorkPackageType.metadata.overall_expiry) {
        const overallValidity = Number(
          selectedWorkPackageType.metadata.overall_expiry.split(" ")[0]
        );
        const overallExpiryDate = subDays(
          add(startDate, { months: overallValidity }),
          1
        );

        return { supportExpiryDate: null, overallExpiryDate };
      }

      return { supportExpiryDate: null, overallExpiryDate: null };
    },
    [workPackages]
  );

  const handleDateChange = React.useCallback(
    (date: [Date | null, Date | null] | Date | null, name: string): void => {
      setContractDetails((prevDetails: typeof contractDetails) => {
        const updatedDetails = {
          ...prevDetails,
          [name]: date,
        };

        // Calculate expiry dates if startDate is being updated and we have a work package type
        if (name === "startDate" && date && prevDetails.workPackageType) {
          const { supportExpiryDate, overallExpiryDate } = calculateExpiryDates(
            date as Date,
            prevDetails.workPackageType
          );

          return {
            ...updatedDetails,
            supportExpiryDate,
            overallExpiryDate,
          };
        }

        return updatedDetails;
      });
    },
    [calculateExpiryDates]
  );

  const onCountrySelect = (data: { label: string; value: string }) => {
    const value = data ? data.value : "";
    setCountry(data);
    setContractDetails((prevDetails: typeof contractDetails) => ({
      ...prevDetails,
      country: value,
    }));
  };

  const onWorkPackageTypeSelect = React.useCallback(
    (data: { label: string; value: string }) => {
      const value = data ? data.value : "";
      setWorkPackage(data);

      const selectedWorkPackageType = workPackages.find(
        (workPackage) => workPackage.value.trim() === value
      );
      const amount = selectedWorkPackageType?.metadata.amount ?? "0";

      setContractDetails((prevDetails: typeof contractDetails) => {
        const updatedDetails = {
          ...prevDetails,
          workPackageType: value,
          workPackageAmount: amount,
        };

        // Calculate expiry dates if we have a start date
        if (prevDetails.startDate) {
          const { supportExpiryDate, overallExpiryDate } = calculateExpiryDates(
            prevDetails.startDate,
            value
          );

          return {
            ...updatedDetails,
            supportExpiryDate,
            overallExpiryDate,
          };
        }

        return updatedDetails;
      });
    },
    [workPackages, calculateExpiryDates]
  );

  if (formPage === "General & Sales Information") {
    return (
      <div className="external-contract-form-container">
        <div className="input-row-wrapper">
          <div className="input-col-wrapper">
            <div className="label-field-container">
              <label className="field-label">
                Company Name<span>*</span>
              </label>
              <TextField
                label=""
                id="companyName"
                name="companyName"
                placeholder="Company Name"
                value={contractDetails.companyName}
                theme="dark"
                onChange={handleChange}
              />
            </div>
          </div>
          <div className="input-col-wrapper">
            <div className="label-field-container">
              <label className="field-label">
                HID Account Manager/PM Email
                <span>*</span>
              </label>
              <TextField
                {...emailStatus}
                helperText={errorMsg.emailMsg}
                label=""
                id="accountManagerEmail"
                name="accountManagerEmail"
                placeholder=""
                value={contractDetails.accountManagerEmail}
                theme="dark"
                onChange={handleChange}
                onBlur={validateEmail}
              />
            </div>
          </div>
        </div>
        <div className="input-row-wrapper">
          <div className="input-col-wrapper">
            <div className="label-field-container">
              <label className="field-label">Salesforce Account</label>
              <TextField
                label=""
                id="salesforceAccount"
                name="salesforceAccount"
                placeholder=""
                value={contractDetails.salesforceAccount}
                theme="dark"
                onChange={handleChange}
              />
            </div>
          </div>
          <div className="input-col-wrapper">
            <div className="label-field-container">
              <label className="field-label">Oracle Channel</label>
              <TextField
                label=""
                id="oracleChannel"
                name="oracleChannel"
                placeholder=""
                value={contractDetails.oracleChannel}
                theme="dark"
                onChange={handleChange}
              />
            </div>
          </div>
        </div>
        <div className="input-row-wrapper">
          <div className="input-col-wrapper">
            <div className="label-field-container">
              <label className="field-label">
                Country<span>*</span>
              </label>
              <Dropdown
                label=""
                id="country"
                name="country"
                options={CountryOptions}
                selected={country}
                onSelect={onCountrySelect}
                menuPortalTarget={document.body}
                isClearable={true}
              />
            </div>
          </div>
          <div className="input-col-wrapper">
            <div className="label-field-container">
              <label className="field-label">
                Region<span>*</span>
              </label>
              <Select
                id="region"
                name="region"
                value={contractDetails.region}
                onChange={handleChange}
                options={RegionOptions}
              />
            </div>
          </div>
        </div>
        <div className="input-row-wrapper">
          <div className="input-col-wrapper">
            <div className="label-field-container">
              <label className="field-label">Salesforce Opportunity</label>
              <TextField
                label=""
                id="salesforceOpportunity"
                name="salesforceOpportunity"
                placeholder=""
                value={contractDetails.salesforceOpportunity}
                theme="dark"
                onChange={handleChange}
              />
            </div>
          </div>
          <div className="input-col-wrapper">
            <div className="label-field-container">
              <label className="field-label">SO Number</label>
              <TextField
                label=""
                id="soNumber"
                name="soNumber"
                placeholder=""
                value={contractDetails.soNumber}
                theme="dark"
                onChange={handleChange}
              />
            </div>
          </div>
        </div>
      </div>
    );
  } else {
    return (
      <div className="external-contract-form-container">
        <div className="input-row-wrapper">
          <div className="label-field-container">
            <label className="field-label">Type of Engagement</label>
            <Select
              id="typeOfEngagement"
              name="typeOfEngagement"
              value={contractDetails.typeOfEngagement}
              onChange={handleChange}
              options={TypeOfEngagementsOptions}
            />
          </div>
        </div>
        <div className="input-row-wrapper">
          <div className="label-field-container">
            <label className="field-label">
              Work Package Type
              <span>*</span>
            </label>
            <Dropdown
              label=""
              id="workPackageType"
              name="workPackageType"
              options={WorkPackageTypeOptions}
              selected={workPackage}
              onSelect={onWorkPackageTypeSelect}
              menuPortalTarget={document.body}
              isClearable={true}
            />
          </div>
        </div>
        <div className="input-row-wrapper">
          <div className="input-col-wrapper">
            <div className="label-field-container">
              <label className="field-label">
                Work Package Amount<span>*</span>
              </label>
              <TextField
                label=""
                id="workPackageAmount"
                name="workPackageAmount"
                placeholder="Enter Amount $"
                value={contractDetails.workPackageAmount}
                theme="dark"
                onChange={handleChange}
              />
            </div>
          </div>
          <div className="input-col-wrapper">
            <div className="label-field-container">
              <label className="field-label">
                Revenue Stream<span>*</span>
              </label>
              <Select
                id="revenueStream"
                name="revenueStream"
                value={contractDetails.revenueStream}
                onChange={handleChange}
                options={RevenueStreamOptions}
              />
            </div>
          </div>
        </div>
        <Divider />
        <div className="date-input-container">
          <div className="date-row-wrapper" style={{ borderTop: "none" }}>
            <label className="date-label">
              Start Date<span>*</span>
            </label>
            <div className="date-value">
              <DatePicker
                onChange={(date) => {
                  handleDateChange(date, "startDate");
                }}
                id="startDate"
                name="startDate"
                label=""
                format="dd-MMM-yyyy"
                placeholder="dd-mmm-yyyy"
                value={contractDetails.startDate}
              />
            </div>
          </div>
          {selectedWorkPackageType?.metadata.support_expiry && (
            <div className="date-row-wrapper">
              <label className="date-label">
                Support Expiry Date<span>*</span>
              </label>
              <div className="date-value">
                <DatePicker
                  onChange={(date) => {
                    handleDateChange(date, "supportExpiryDate");
                  }}
                  id="supportExpiryDate"
                  name="supportExpiryDate"
                  label=""
                  format="dd-MMM-yyyy"
                  placeholder="dd-mmm-yyyy"
                  value={contractDetails.supportExpiryDate}
                />
              </div>
            </div>
          )}
          <div className="date-row-wrapper">
            <label className="date-label">
              Overall Expiry Date<span>*</span>
            </label>
            <div className="date-value">
              <DatePicker
                onChange={(date) => {
                  handleDateChange(date, "overallExpiryDate");
                }}
                id="overallExpiryDate"
                name="overallExpiryDate"
                label=""
                format="dd-MMM-yyyy"
                placeholder="dd-mmm-yyyy"
                value={contractDetails.overallExpiryDate}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }
};

export default ExternalContractForm;
